import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import type {
  EventResponse,
  FailureCategoryR,
  JobDetail,
  Severity,
} from '../api/types';
import { FAILURE_CATEGORY_R_IS_REAL_FAILURE } from '../api/types';
import { BaseStationsMap } from '../components/BaseStationsMap';
import { ConvergenceSliceCard } from '../components/ConvergenceSliceCard';
import { EmptyState } from '../components/EmptyState';
import { GnssStack } from '../components/GnssStack';
import { Ic } from '../components/Icons';
import { StatusPill } from '../components/StatusPill';
import { TimelineItem } from '../components/Timeline';
import { useJob } from '../hooks/useJob';
import { useJobEvents } from '../hooks/useEvents';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { downloadFileViaPlatform } from '../lib/download';
import { renderError } from '../lib/error_display';
import {
  drClass,
  fmtResidual,
  formatDateTime,
  formatDuration,
  formatTimeMoscow,
} from '../lib/format';
import { sortGnssSystems } from '../lib/gnss_order';
import { translateJobState } from '../lib/state_labels';

import '../components/DetailScreen.css';

/** Full job-detail screen — the route ``/job/:jobId`` resolves to this.
 *
 *  Layout in five blocks (some conditional):
 *    1. ``DetailHero``      — station + GNSS + obs_date + #id + meta row.
 *    2. ``DetailService``   — service_task_id / service_state (SUBMITTED only).
 *    3. ``DetailResult``    — residual_3d_mm + file stub buttons (DONE only).
 *    4. ``DetailError``     — full error message + stripped summary (FAILED only).
 *    5. ``DetailTimeline``  — events for this job from /api/v1/events.
 *
 *  Phase 3.3: cancel/retry/download actions are stubs (``alert``).
 *  Phase 3.5 will wire them to real API endpoints.
 */
export function DetailScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { tg } = useTelegramWebApp();
  const jobQuery = useJob(jobId);
  const eventsQuery = useJobEvents(jobId);

  // Telegram BackButton wiring — shows the system back-arrow in the
  // WebApp header. On dev (no tg) we still expose the custom .back-btn
  // in the header, so this useEffect is a no-op for the browser case.
  useEffect(() => {
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => {
      if (window.history.length > 1) navigate(-1);
      else tg.close();
    };
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton?.hide();
      tg.BackButton?.offClick(onBack);
    };
  }, [tg, navigate]);

  if (jobQuery.isLoading) {
    return (
      <>
        <DetailHeader jobId={jobId ?? '?'} onBack={() => navigate(-1)} />
        <div className="screen">
          <EmptyState>загрузка…</EmptyState>
        </div>
      </>
    );
  }
  if (jobQuery.isError || !jobQuery.data) {
    const err = jobQuery.error;
    const msg =
      err instanceof ApiError && err.status === 404
        ? `задача #${jobId} не найдена`
        : err instanceof Error
          ? err.message
          : 'ошибка загрузки задачи';
    return (
      <>
        <DetailHeader jobId={jobId ?? '?'} onBack={() => navigate(-1)} />
        <div className="screen">
          <EmptyState>{msg}</EmptyState>
        </div>
      </>
    );
  }

  const job = jobQuery.data;
  return (
    <>
      <DetailHeader
        jobId={String(job.id)}
        state={job.state}
        errorMessage={job.error_message ?? null}
        failureCategory={job.failure_category}
        onBack={() => navigate(-1)}
      />
      <div className="screen">
        <DetailHero job={job} />
        {/* Phase C — convergence slice card. Renders only when this
            job is a slice (slice_length_nominal_h is set); a normal
            daily job leaves the slot empty. */}
        <ConvergenceSliceCard job={job} />
        <DetailRinexValidation job={job} />
        {job.service_deleted_at && (
          <div
            className="cleanup-mark"
            title={`Задача удалена на сервере обработки: ${formatDateTime(job.service_deleted_at)}`}
          >
            🧹 убрано с сервера обработки
          </div>
        )}
        {job.state === 'submitted' && <DetailService job={job} />}
        {job.state === 'done' && <DetailResult job={job} />}
        {/* Phase 4.6 — IGS coords "эталон" card sits AFTER the
            «результат» block so the operator reads "what we computed"
            before "what we compared it to". Renders only when the
            residual carries non-null IGS coords (i.e. the new
            methodology produced the row, AND it was either freshly
            inserted post-4.6 or was backfilled by ``backfill_igs_metadata``).
            For legacy ``itrf2014_propagated`` rows the IGS triple is
            NULL and the block is hidden. */}
        {job.state === 'done' && <ReferenceBlock job={job} />}
        {/* Phase 4.6 — for DONE jobs with no residual row AND a
            stamped ``residual_skip_reason``, the «эталон» card slot
            is filled by an info-block explaining why no comparison
            happened. Mutually exclusive with the ReferenceBlock —
            both branches check the same residuals-empty condition. */}
        {job.state === 'done' && <ResidualSkipInfoBlock job={job} />}
        {/* Phase 4.12 — the in-card ``<DetailError>`` block (and its
            duplicate «кратко: …» line) is gone. The error surface
            collapses to the new pulsing-badge button in the header
            (``ErrorStatusButton``); clicking the badge expands a
            panel with the short Russian phrase + a <details>
            wrapper around the full technical text. */}
        <DetailSubstitution job={job} />
        <DetailRetryChain job={job} />
        {/* Phase H — оporные станции с мини-картой геометрии сети.
            Блок скрываем целиком когда base_stations пустой
            (legacy задачи до Phase A) — нет смысла показывать
            пустой раздел. */}
        {(job.base_stations?.length ?? 0) > 0 && (
          <BaseStationsMap
            target={{
              code: job.station_code ?? '—',
              latitude: job.station_latitude ?? null,
              longitude: job.station_longitude ?? null,
            }}
            references={job.base_stations ?? []}
          />
        )}
        <DetailRinexHeader job={job} />
        <DetailTimeline events={eventsQuery.data?.items ?? []} />
      </div>
      <DetailActions job={job} />
    </>
  );
}

// ----- subcomponents -----

function DetailHeader({
  jobId,
  state,
  errorMessage,
  failureCategory,
  onBack,
}: {
  jobId: string;
  state?: JobDetail['state'];
  /** Phase 4.12 — the failed-job error_message that drives the
   *  expandable error panel. ``null`` for non-failed jobs (or for
   *  failed jobs with no message — defensive only; the scheduler
   *  always writes one). */
  errorMessage?: string | null;
  /** Phase R fix — a FAILED job whose category is NOT a real failure
   *  (not_applicable / file_unavailable / no_reference / other) is a
   *  catalogue/infrastructure limit, not an error. Drives the neutral
   *  badge, mirroring the feed (JobCard / StatusPill, FIX 1). */
  failureCategory?: FailureCategoryR | null;
  onBack: () => void;
}) {
  // Same predicate StatusPill / JobCard use, so the detail badge agrees
  // with the feed: a neutral-category failure renders «не применимо»
  // (etc.), never the red «ОШИБКА». NULL category falls through to the
  // legacy real-failure path.
  const isNeutralFailure =
    state === 'failed' &&
    failureCategory != null &&
    FAILURE_CATEGORY_R_IS_REAL_FAILURE[failureCategory] === false;

  return (
    <div className="detail-header">
      <button className="back-btn" onClick={onBack} aria-label="Назад">
        <Ic.back />
      </button>
      <div className="id">задача #{jobId}</div>
      {/* Phase 4.12 — a REAL-failure FAILED job with an error_message
          uses the interactive ErrorStatusButton («ОШИБКА» + expandable
          details). Neutral-category failures and every non-failed state
          use the StatusPill, which renders the neutral category label
          when given ``failureCategory``. */}
      {state === 'failed' && errorMessage && !isNeutralFailure ? (
        <ErrorStatusButton errorMessage={errorMessage} />
      ) : (
        state && <StatusPill state={state} failureCategory={failureCategory} />
      )}
    </div>
  );
}

/** Phase 4.12 — pulsing-dot status badge that expands to a panel
 *  carrying the rendered error.
 *
 *  Composes the renderError catalogue: the button face shows
 *  «● ОШИБКА» with a CSS-animated dot, and on click an absolute-
 *  positioned panel appears below carrying:
 *
 *    1. The ``.shortText`` (Russian, no technical noise),
 *    2. A native ``<details>`` element exposing ``.detailText``
 *       (full prefix-stripped error_message) under «технические
 *       детали». ``<details>`` gives us keyboard + screen-reader
 *       support for free.
 *
 *  When ``renderError`` returns ``null`` (empty message) the button
 *  falls back to a non-interactive «ОШИБКА» pill — the caller
 *  already guards against this case but we double-check. */
function ErrorStatusButton({ errorMessage }: { errorMessage: string }) {
  const [expanded, setExpanded] = useState(false);
  const rendered = renderError(errorMessage);

  if (!rendered) {
    return <StatusPill state="failed" />;
  }
  return (
    <div className="error-status-container">
      <button
        type="button"
        className="error-status-button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Скрыть детали ошибки' : 'Показать детали ошибки'}
        title={
          expanded
            ? 'Скрыть детали ошибки'
            : 'Нажмите, чтобы показать детали'
        }
      >
        <span className="error-pulse-dot" aria-hidden />
        <span className="error-label">ОШИБКА</span>
      </button>
      {expanded && (
        <div className="error-detail-panel" role="region">
          <div className="error-short">{rendered.shortText}</div>
          <details className="error-tech-details">
            <summary>технические детали</summary>
            <pre className="error-detail-text">{rendered.detailText}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

function DetailHero({ job }: { job: JobDetail }) {
  // Phase 3.7 — handcrafted IGS-station human name from
  // ``api.lib.station_names``. Falls back to code-only when the
  // station isn't in the curated JSON.
  const subName = job.station_full_name
    ? job.station_country
      ? `${job.station_full_name}, ${job.station_country}`
      : job.station_full_name
    : null;
  return (
    <div className="detail-hero">
      <div className="hero-head">
        <h1 className="station-name">
          {/* Phase 4.11 — station code as a click-to-filter link.
              Lands on the Feed with an exact-match query (?q="CODE")
              so the operator sees ALL jobs for this station. Rendered as a
              react-router <Link> so the same-origin nav is client-side — a raw
              <a href> would do a FULL document reload (react-router does NOT
              intercept plain anchors), wiping the query cache + re-auth. */}
          {job.station_code ? (
            <Link
              to={feedExactHref(job.station_code)}
              className="station-code-link"
              title={`показать все задачи станции ${job.station_code}`}
            >
              {job.station_code}
            </Link>
          ) : (
            '—'
          )}
        </h1>
        {subName && <span className="station-sub-name">· {subName}</span>}
        <GnssStack systems={job.gnss_systems} />
      </div>
      <div className="meta-row">
        <Meta label="дата" value={job.obs_date ?? '—'} />
        {/* Phase 4.11 — tag as a click-to-filter link. Same exact-
            match form as the station-code header above so the
            operator can pivot from a single job to its whole batch
            in one click. */}
        <MetaTag label="тег" tag={job.tag} />
        <Meta
          label="длительность"
          value={
            job.submitted_at
              ? formatDuration(job.submitted_at, job.finished_at ?? null)
              : '—'
          }
        />
        <Meta label="режим" value={job.mode} />
        <Meta label="модель" value={job.tectonic_model} />
        <Meta label="создана" value={formatDateTime(job.created_at)} />
        {job.attempts > 1 && <Meta label="попыток" value={String(job.attempts)} />}
      </div>
    </div>
  );
}

/** Phase 4.11 — Feed deep-link with an exact-match query parameter.
 *  Wraps the value in double quotes (the parser's exact-match
 *  signal) and URL-encodes the whole thing so the ``"`` and any
 *  special characters in tags (``+``, ``.``) round-trip safely. */
function feedExactHref(term: string): string {
  return `/?q=${encodeURIComponent(`"${term}"`)}`;
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="meta-item">
      <div className="l">{label}</div>
      <div className="v">{value}</div>
    </div>
  );
}

/** Phase 4.11 — tag variant of Meta that renders the value as a
 *  click-to-filter link. Falls back to the dash placeholder when
 *  ``tag`` is null/empty (matches the legacy Meta rendering). */
function MetaTag({ label, tag }: { label: string; tag: string | null | undefined }) {
  return (
    <div className="meta-item">
      <div className="l">{label}</div>
      <div className="v">
        {tag ? (
          <Link
            to={feedExactHref(tag)}
            className="tag-link"
            title={`показать все задачи серии ${tag}`}
          >
            {tag}
          </Link>
        ) : (
          '—'
        )}
      </div>
    </div>
  );
}

/** Phase 4.1 — pre-flight RINEX content validation plate.
 *
 *  The scheduler walks the RINEX header before submit and stamps one
 *  of four ``rinex_validation_status`` values on the job. Two of them
 *  warrant a visible explanation:
 *
 *  * ``"incompatible"`` — none of the requested constellations is in
 *    the file. The job is already in the ``failed`` state with a
 *    ``[permanent]`` marker; this plate explains *why* in operator-
 *    facing Russian. Rendered in red so it doesn't blend with the
 *    blue diagnostic-notice plate.
 *  * ``"subset"`` — at least one requested constellation is missing.
 *    PPP still ran on the intersection; the plate calls out which
 *    constellations went missing and which actually got processed.
 *    Amber palette.
 *
 *  ``"valid"`` and ``"validation_failed"`` render nothing — they're
 *  not actionable for the operator.
 */
function DetailRinexValidation({ job }: { job: JobDetail }) {
  const status = job.rinex_validation_status;
  if (status !== 'incompatible' && status !== 'subset') return null;

  // Phase 4.9 — sort every list through ``sortGnssSystems`` so the
  // comma-separated strings in this plate respect the same canonical
  // order as the chip-row right above it (GPS, GLO, GAL, BDS, …).
  const requested = sortGnssSystems(job.gnss_systems);
  const actual = sortGnssSystems(job.actual_gnss_systems ?? []);
  const missing = requested.filter((s) => !actual.includes(s));
  const processed = requested.filter((s) => actual.includes(s));

  if (status === 'incompatible') {
    return (
      <div className="rinex-incompatible" role="status">
        ℹ В RINEX-файле станции отсутствуют запрошенные GNSS-системы:{' '}
        {requested.join(', ') || '—'}. Эта конфигурация не может быть
        обработана PPP-сервисом — задача завершена pre-flight проверкой.
        Остальные задачи серии продолжаются.
        {actual.length > 0 && (
          <>
            {' '}
            В файле есть: {actual.join(', ')}.
          </>
        )}
      </div>
    );
  }
  return (
    <div className="rinex-subset" role="status">
      ⚠ <strong>В RINEX-файле отсутствуют системы:</strong>{' '}
      {missing.join(', ')}. PPP обработал только: {processed.join(', ')}.
      <br />
      Полный состав файла: {actual.join(', ')}.
    </div>
  );
}

function DetailService({ job }: { job: JobDetail }) {
  return (
    <div className="detail-block detail-service">
      <div className="section-head">
        <span className="left">сервис PPP Online</span>
      </div>
      <div className="kv">
        <span className="k">task id</span>
        <span className="v">{job.service_task_id ?? '—'}</span>
        {job.service_uuid && (
          <>
            <span className="k">uuid</span>
            <span className="v">{job.service_uuid}</span>
          </>
        )}
        {job.service_state && (
          <>
            <span className="k">state</span>
            <span className="v">{job.service_state}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** Phase 4.6 — UI mode for the ResidualBlock per-axis grid.
 *  ``enu`` = topocentric (dN/dE/dU); ``xyz`` = geocentric (dX/dY/dZ).
 *  |dR| is identical in both views. Persisted in localStorage under
 *  ``RESIDUAL_MODE_STORAGE_KEY`` so the toggle survives reloads. */
type ResidualMode = 'enu' | 'xyz';
const RESIDUAL_MODE_STORAGE_KEY = 'gnss-qa:residual-mode';

function loadResidualMode(): ResidualMode {
  try {
    const saved = localStorage.getItem(RESIDUAL_MODE_STORAGE_KEY);
    return saved === 'xyz' ? 'xyz' : 'enu';
  } catch {
    // localStorage can throw in privacy-mode browsers; default ENU.
    return 'enu';
  }
}

function saveResidualMode(mode: ResidualMode): void {
  try {
    localStorage.setItem(RESIDUAL_MODE_STORAGE_KEY, mode);
  } catch {
    // Silently ignore — the toggle still works for the session.
  }
}

function DetailResult({ job }: { job: JobDetail }) {
  const cls = drClass(job.residual_3d_mm);
  // Phase 3.13.2 — show the quality-warning plate when any per-catalogue
  // residual on this job is flagged (catalogue-collision case: P117 /
  // P101). The plate sits between the |dR| value and the file buttons
  // so the user sees the explanation before they decide whether to
  // download the report.
  const hasAnomaly =
    job.residuals?.some((r) => r.data_quality_warning) ?? false;
  // Phase 3.16 v2 — prefer the IGS Daily SINEX residual when
  // present (the new methodology). The previous flow stored one
  // residual per catalogue (ITRF2014/ITRF2020); post-3.16v2 only
  // one row exists per job, labelled "IGS_DAILY". Older jobs that
  // haven't been backfilled yet still carry the legacy rows and
  // render the same per-axis breakdown for symmetry.
  const igsResidual =
    job.residuals?.find((r) => r.comparison_source === 'igs_daily_sinex') ??
    job.residuals?.[0];
  // Header label reflects which methodology actually produced the
  // shown numbers. The detailed source note below the values then
  // spells it out in operator-readable Russian.
  const headerLabel =
    igsResidual?.comparison_source === 'igs_daily_sinex'
      ? 'IGS Daily'
      : igsResidual?.catalog ?? 'IGS Daily';
  // Phase 4.6 — ENU/XYZ toggle. Persisted in localStorage so it
  // sticks across detail screens. Default is ENU per spec.
  const [mode, setMode] = useState<ResidualMode>(() => loadResidualMode());
  const setModeAndSave = (next: ResidualMode) => {
    setMode(next);
    saveResidualMode(next);
  };
  return (
    <div className="detail-block">
      <div className="section-head">
        <span className="left">результат</span>
        <span className="right">
          {igsResidual && (
            <ResidualToggle mode={mode} onChange={setModeAndSave} />
          )}
          <span className="source-badge">{headerLabel}</span>
        </span>
      </div>
      <div className="dr-single">
        <div className="label">|dr| · 3D</div>
        <div className={`value ${cls}`}>
          {fmtResidual(job.residual_3d_mm)}
          <span className="unit">мм</span>
        </div>
      </div>
      {/* Phase B Block 1 — service self-estimate (СКО), shown right
          below the external |dR|: the two numbers are «what the
          reference says» vs «what the service thinks of itself».
          Sentinel σ (identical triple / >1 m / missing) renders
          «оценка недоступна» — never the garbage value (job 1349's
          12247 mm). For a DONE job with no эталон the |dR| above is
          «—» and this is the only quality figure on the card. */}
      <SolutionQualityLine job={job} />
      {/* Phase 3.16 v2 + 4.6 — per-axis residuals. The toggle
          flips between dN/dE/dU and dX/dY/dZ; |dR| above stays
          identical. The methodology note below mirrors the
          toggle state so the operator sees WHICH breakdown is
          on screen at a glance. */}
      {igsResidual && (
        <>
          <div className="residual-axes" data-testid="residual-axes">
            {mode === 'enu' ? (
              <>
                <div className="residual-axis">
                  <span className="label">dN</span>
                  <span className="value">{fmtResidual(igsResidual.dn_mm)}</span>
                  <span className="unit">мм</span>
                </div>
                <div className="residual-axis">
                  <span className="label">dE</span>
                  <span className="value">{fmtResidual(igsResidual.de_mm)}</span>
                  <span className="unit">мм</span>
                </div>
                <div className="residual-axis">
                  <span className="label">dU</span>
                  <span className="value">{fmtResidual(igsResidual.du_mm)}</span>
                  <span className="unit">мм</span>
                </div>
              </>
            ) : (
              <>
                <div className="residual-axis">
                  <span className="label">dX</span>
                  <span className="value">{fmtResidual(igsResidual.dx_mm)}</span>
                  <span className="unit">мм</span>
                </div>
                <div className="residual-axis">
                  <span className="label">dY</span>
                  <span className="value">{fmtResidual(igsResidual.dy_mm)}</span>
                  <span className="unit">мм</span>
                </div>
                <div className="residual-axis">
                  <span className="label">dZ</span>
                  <span className="value">{fmtResidual(igsResidual.dz_mm)}</span>
                  <span className="unit">мм</span>
                </div>
              </>
            )}
          </div>
          <div className="comparison-source-note" role="note">
            {igsResidual.comparison_source === 'igs_daily_sinex' ? (
              <>
                сравнение с IGS Daily Combined SINEX на эпоху наблюдений
                {' · '}
                {mode === 'enu'
                  ? 'топоцентрические компоненты'
                  : 'геоцентрические компоненты'}
              </>
            ) : (
              <>
                сравнение с {igsResidual.catalog} (старая методика —
                propagated; будет перерасчитано)
              </>
            )}
          </div>
        </>
      )}
      {hasAnomaly && (
        <div className="quality-warning" role="status">
          ⚠ <strong>Аномалия расхождения:</strong> значение |dR|
          превышает 100 м, что нетипично для PPP-обработки. Вероятно,
          координаты эталона в каталоге не соответствуют реально
          обработанной станции (коллизия 4-character кодов между
          разными станциями IGS). Это будет исправлено в Phase 4.0
          (миграция на 9-character ID).
        </div>
      )}
      {/* Phase 3.15 — surface the diagnostic-preservation case
          (compute_failed result kept on PPP for the service author).
          The reason field is currently always ``"compute_failed"`` when
          ``preserve_on_ppp`` is true, but we render it conditionally so
          future reasons (corrupt_output, empty_residuals, ...) slot in
          without code changes here. */}
      {job.preserve_on_ppp && (
        <div className="diagnostic-notice" role="status">
          🔬 <strong>Сохранена на PPP-сервисе для диагностики</strong>
          {job.preserve_reason === 'compute_failed' && (
            <> (причина: «Не удалось вычислить координаты»)</>
          )}
          . Cleanup пропущен — задача остаётся доступной разработчику
          сервиса для разбора.
        </div>
      )}
      {(job.report_txt_path || job.catalog_csv_path || job.track_csv_path) && (
        <>
          <div style={{ height: 12 }} />
          <div className="detail-files">
            {job.report_txt_path && (
              <DownloadButton jobId={job.id} kind="report" label="report.txt" hint={job.report_txt_path} />
            )}
            {job.catalog_csv_path && (
              <DownloadButton jobId={job.id} kind="catalog" label="catalog.csv" hint={job.catalog_csv_path} />
            )}
            {job.track_csv_path && (
              <DownloadButton jobId={job.id} kind="track" label="track.csv" hint={job.track_csv_path} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Phase B Block 1 — the «СКО · оценка сервиса» line under |dR|.
 *
 *  Two render branches, per spec §1.2:
 *    * ``sigma_is_sentinel`` → «оценка недоступна» (grey) — the σ
 *      triple is a service stub, so showing the number would
 *      mislead the operator (job 1349's 12247 mm).
 *    * a real σ → the value in mm.
 *  A DONE job with no σ at all is sentinel by construction
 *  (``is_sentinel_sigma`` treats ``null`` as a stub), so these two
 *  branches cover every case. When the job has no эталон the |dR|
 *  line above already reads «—» and this σ is the only quality
 *  figure — same render, no special-casing needed (spec §1.2).
 *
 *  Optional §1.3 — when both |dR| and σ are present and the measured
 *  error exceeds 3 × σ, a small notice flags the over-confidence
 *  case the LCK4 / STAS investigation surfaced. */
function SolutionQualityLine({ job }: { job: JobDetail }) {
  const overconfident =
    !job.sigma_is_sentinel &&
    job.sigma_3d_mm != null &&
    job.residual_3d_mm != null &&
    job.residual_3d_mm > 3 * job.sigma_3d_mm;
  // Phase G.5 — σ per-axis breakdown only meaningful when σ is
  // non-sentinel AND all three axis components are present. Sentinel
  // case (job 1349 etc.) collapses to «оценка недоступна» without
  // a breakdown, matching the existing aggregate-row behaviour.
  const hasAxes =
    !job.sigma_is_sentinel &&
    job.sigma_x_m != null &&
    job.sigma_y_m != null &&
    job.sigma_z_m != null;
  return (
    <>
      <div className="sigma-line" data-testid="sigma-line">
        <span className="sigma-label">СКО · оценка сервиса</span>
        {job.sigma_is_sentinel ? (
          <span
            className="sigma-value unavailable"
            data-testid="sigma-unavailable"
          >
            оценка недоступна
          </span>
        ) : (
          <span className="sigma-value" data-testid="sigma-value">
            {fmtResidual(job.sigma_3d_mm)}
            <span className="unit">мм</span>
          </span>
        )}
      </div>
      {/* Phase G.5 — per-axis sigma + the actual formula the
          aggregate row collapses to. Operator can now see «сколько
          вклада дала каждая ось» and verify the √(σx²+σy²+σz²)
          arithmetic instead of trusting the single mm-number. */}
      {hasAxes && (
        <div className="sigma-breakdown" data-testid="sigma-breakdown">
          <div className="sigma-axes" data-testid="sigma-axes">
            <span className="sigma-axis">
              <span className="sigma-axis-label">σX:</span>
              <span className="sigma-axis-value">
                {(job.sigma_x_m as number).toFixed(3)}
              </span>
            </span>
            <span className="sigma-axis">
              <span className="sigma-axis-label">σY:</span>
              <span className="sigma-axis-value">
                {(job.sigma_y_m as number).toFixed(3)}
              </span>
            </span>
            <span className="sigma-axis">
              <span className="sigma-axis-label">σZ:</span>
              <span className="sigma-axis-value">
                {(job.sigma_z_m as number).toFixed(3)}
              </span>
            </span>
            <span className="sigma-axis-unit">м · из отчёта</span>
          </div>
          <div className="sigma-formula" data-testid="sigma-formula">
            σ_3D = √(σX² + σY² + σZ²) × 1000 ={' '}
            {fmtResidual(job.sigma_3d_mm)} мм
          </div>
        </div>
      )}
      {overconfident && (
        <div
          className="sigma-warning"
          role="status"
          data-testid="sigma-warning"
        >
          ⚠ реальная ошибка |dR| более чем втрое превышает оценку
          сервиса — самооценка слишком оптимистична
        </div>
      )}
    </>
  );
}

/** Phase 4.6 — small two-button toggle that flips the ResidualBlock
 *  between geodetic (ENU) and geocentric (XYZ) breakdowns. Sits in
 *  the right side of the «результат» section head. Uses the existing
 *  parchment-+-ink palette tokens (--surface-2, --line-soft) — no
 *  saturated accent fills per the Phase 4.13.2 design system. */
function ResidualToggle({
  mode,
  onChange,
}: {
  mode: ResidualMode;
  onChange: (mode: ResidualMode) => void;
}) {
  return (
    <span
      className="residual-toggle"
      role="group"
      aria-label="Переключение системы координат для разностей"
    >
      <button
        type="button"
        className={`residual-toggle-btn ${mode === 'enu' ? 'active' : ''}`}
        onClick={() => onChange('enu')}
        aria-pressed={mode === 'enu'}
        aria-label="Топоцентрические компоненты (dN/dE/dU)"
      >
        ENU
      </button>
      <button
        type="button"
        className={`residual-toggle-btn ${mode === 'xyz' ? 'active' : ''}`}
        onClick={() => onChange('xyz')}
        aria-pressed={mode === 'xyz'}
        aria-label="Геоцентрические компоненты (dX/dY/dZ)"
      >
        XYZ
      </button>
    </span>
  );
}

/** Phase 4.6 + G.4 — paired «ВЫЧИСЛЕНО (сервис)» + «ЭТАЛОН» cards.
 *
 *  Renders one or two collapsible cards depending on what's available:
 *
 *    * Always (when the residual exists): «ВЫЧИСЛЕНО (сервис)» —
 *      ECEF X/Y/Z that PPP actually returned for this job. Default
 *      OPEN — this is the primary verification surface (operator
 *      glances at the value to catch parser bugs).
 *    * When IGS Daily Combined SINEX coords are present: «ЭТАЛОН»
 *      — same X/Y/Z format from the SINEX +SOLUTION/ESTIMATE block,
 *      plus the source label + file date + optional FALLBACK badge.
 *      Default COLLAPSED (per spec §G.4 — карточка не должна быть
 *      перегружена; нужен — раскрыл).
 *
 *  Both cards use the native ``<details>`` element with the same
 *  ``.section-head`` summary pattern as ШАПКА RINEX
 *  (DetailRinexHeader) so the chrome stays consistent. Coordinates
 *  print at ``.toFixed(3)`` in both — identical formatting lets the
 *  operator do line-by-line visual diff (X with X, Y with Y).
 *
 *  Phase G.4 fully replaces the previous one-card ReferenceBlock —
 *  «ВЫЧИСЛЕНО» didn't exist there at all, so operators had no way
 *  to spot a съехавшую запятую in the parser without diving into the
 *  catalog CSV. */
function ReferenceBlock({ job }: { job: JobDetail }) {
  const igsResidual =
    job.residuals?.find((r) => r.comparison_source === 'igs_daily_sinex') ??
    job.residuals?.[0];
  if (!igsResidual) return null;
  const hasService =
    igsResidual.service_x_m != null &&
    igsResidual.service_y_m != null &&
    igsResidual.service_z_m != null;
  const hasIgs =
    igsResidual.igs_x_m != null &&
    igsResidual.igs_y_m != null &&
    igsResidual.igs_z_m != null;
  if (!hasService && !hasIgs) return null;
  const sinexDate = igsResidual.igs_sinex_date;
  const isFallback =
    sinexDate != null && job.obs_date != null && sinexDate !== job.obs_date;

  return (
    <>
      {hasService && (
        <details
          className="detail-block coords-block coords-block-service"
          data-testid="coords-block-service"
          open
        >
          <summary className="section-head coords-block-summary">
            <span className="left">▾ ВЫЧИСЛЕНО (сервис)</span>
            <span className="right">
              <span className="source-badge">PPP</span>
            </span>
          </summary>
          <div className="igs-coords">
            <CoordRow label="X" value={igsResidual.service_x_m as number} />
            <CoordRow label="Y" value={igsResidual.service_y_m as number} />
            <CoordRow label="Z" value={igsResidual.service_z_m as number} />
          </div>
        </details>
      )}
      {hasIgs && (
        <details
          className="detail-block coords-block coords-block-reference"
          data-testid="coords-block-reference"
        >
          <summary className="section-head coords-block-summary">
            <span className="left">▸ ЭТАЛОН</span>
            <span className="right">
              <span className="source-badge">IGS DAILY</span>
              {sinexDate && (
                <span className="sinex-date" title="Дата файла SINEX">
                  {sinexDate}
                </span>
              )}
              {isFallback && (
                <span
                  className="fallback-badge"
                  title="Координаты взяты из соседнего дня (станция отсутствовала в SINEX за дату наблюдения)"
                >
                  FALLBACK
                </span>
              )}
            </span>
          </summary>
          <div className="igs-coords">
            <CoordRow label="X" value={igsResidual.igs_x_m as number} />
            <CoordRow label="Y" value={igsResidual.igs_y_m as number} />
            <CoordRow label="Z" value={igsResidual.igs_z_m as number} />
          </div>
        </details>
      )}
    </>
  );
}

/** One axis row inside an `.igs-coords` block. Pulled out so the
 *  ВЫЧИСЛЕНО / ЭТАЛОН blocks render with identical formatting
 *  (3-decimal fixed; metres unit), which is what makes the visual
 *  line-by-line diff in G.4 actually work. */
function CoordRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="coord-row">
      <span className="coord-label">{label}:</span>
      <span className="coord-value">{value.toFixed(3)}</span>
      <span className="coord-unit">м</span>
    </div>
  );
}

/** Phase 4.6 — info-block surface for DONE jobs without a residual.
 *
 *  Renders ONLY when the job has no residual rows AND a stamped
 *  ``residual_skip_reason``. Mutually exclusive with ReferenceBlock
 *  by the residuals-empty check. Vocabulary mirror of the scheduler
 *  constants in ``_residual_compute.SKIP_REASON_*``. */
function ResidualSkipInfoBlock({ job }: { job: JobDetail }) {
  if (job.residuals && job.residuals.length > 0) return null;
  // P1 · 2B — pick the info kind from the server-computed residual_display
  // class; fall back to the legacy skip_reason when the class is absent (an
  // old/cached response). operational_failure (401/timeout) now surfaces its
  // own block — the writer maps those to residual_skip_reason=NULL, so the old
  // reason-only block would have shown NOTHING.
  type Kind = 'absent' | 'compute_failed' | 'operational' | 'unknown';
  let kind: Kind | null = null;
  const display = job.residual_display;
  if (display) {
    if (display === 'absent') kind = 'absent';
    else if (display === 'compute_failed') kind = 'compute_failed';
    else if (display === 'operational_failure') kind = 'operational';
    else if (display === 'unknown') kind = 'unknown';
    else return null; // residual / unprocessed → no info block
  } else {
    const reason = job.residual_skip_reason;
    if (reason === 'no_igs_reference_within_window') kind = 'absent';
    else if (reason === 'compute_failed_no_coords') kind = 'compute_failed';
    else if (reason) kind = 'unknown';
    else return null;
  }
  return (
    <div className="detail-block info-block">
      <div className="section-head">
        <span className="left">эталон</span>
      </div>
      {kind === 'absent' && (
        <p className="info-text">
          Координаты станции {job.station_code ?? '—'} не найдены в IGS
          Daily Combined SINEX за {job.obs_date ?? '—'} и ±5 соседних
          дней. Сравнение невозможно — станция, вероятно, не участвует
          в IGS combined решении.
        </p>
      )}
      {kind === 'compute_failed' && (
        <p className="info-text">
          PPP-сервис вернул отчёт без координат («Не удалось вычислить
          координаты»). Сравнение невозможно.
        </p>
      )}
      {kind === 'operational' && (
        <p className="info-text">
          Не удалось получить эталон — техническая ошибка
          {job.residual_failure_code ? ` (${job.residual_failure_code})` : ''}.
          Сравнение отложено; требуется повтор или действие оператора.
        </p>
      )}
      {kind === 'unknown' && (
        <p className="info-text">
          Состояние эталона не определено — проверьте задачу.
        </p>
      )}
    </div>
  );
}

function DownloadButton({
  jobId,
  kind,
  label,
  hint,
}: {
  jobId: number;
  kind: 'report' | 'catalog' | 'track';
  label: string;
  hint: string;
}) {
  const { tg } = useTelegramWebApp();
  const onClick = async () => {
    try {
      // Phase 4.8b — platform-aware download. On Android / iOS
      // Telegram the helper routes through ``tg.openLink`` (which
      // hands the URL to the device browser where it actually
      // downloads); on Telegram Desktop / web / plain browser tabs
      // it keeps the legacy fetch+blob+anchor path. The query-param
      // init_data fallback in ``api.dependencies.get_current_user``
      // accepts the credential when the device browser can't send
      // headers attached to fetch().
      await downloadFileViaPlatform(
        api.fileUrl(jobId, kind),
        tg?.initData ?? '',
        { tg },
      );
      tg?.HapticFeedback?.impactOccurred('light');
    } catch (err) {
      tg?.HapticFeedback?.notificationOccurred('error');
      const msg =
        err instanceof ApiError
          ? `Ошибка ${err.status}: ${err.message}`
          : 'Не удалось скачать файл';
      if (tg?.showAlert) tg.showAlert(msg);
      else alert(msg);
    }
  };
  return (
    <button type="button" className="file-btn" title={hint} onClick={onClick}>
      <span className="icon">📄</span>
      {label}
    </button>
  );
}

// Phase 4.12 — the legacy ``<DetailError>`` middle-of-card block
// (and its «кратко: …» line) is removed. The error surface lives
// in the header now via ``<ErrorStatusButton>``, which routes the
// raw ``error_message`` through ``renderError`` from
// ``lib/error_display.ts`` and exposes the rendered short text +
// technical details in an absolutely-positioned panel below the
// pulsing badge. See ``ErrorStatusButton`` above.

// Phase 4.7 — auto-substitute provenance block.
//
// Two mutually-exclusive renders:
//
//   * Substitute job (``substitution_parent_id`` is set): show
//     «🔄 Эта задача — автозамена для #N (попытка X из 3)».
//   * Original that got substitutes (``substitution_children`` non-empty):
//     show «🔄 Заменена на: #M1 (KOUR), #M2 (BOR1)» — each link is
//     a Mini App deep-link to the child's detail screen.
//
// Both flavours use the amber ``.substitution-info`` palette (same
// design token as ``.rinex-subset`` / ``.plate-model-outside``) —
// auto-substitute is the user-positive outcome of an upstream
// failure, so the amber wording is "informational notice", not "error".
function DetailSubstitution({ job }: { job: JobDetail }) {
  const MAX_ATTEMPTS = 3; // mirrors scheduler._flow.MAX_SUBSTITUTION_ATTEMPTS
  if (job.substitution_parent_id) {
    return (
      <div className="detail-block">
        <div className="section-head">
          <span className="left">автозамена</span>
        </div>
        <div className="substitution-info" role="status">
          🔄 Эта задача — автозамена для задачи{' '}
          <Link to={`/job/${job.substitution_parent_id}`}>
            #{job.substitution_parent_id}
          </Link>
          {' '}(попытка {job.substitution_attempt ?? 1} из {MAX_ATTEMPTS}).
        </div>
      </div>
    );
  }
  const children = job.substitution_children ?? [];
  if (children.length > 0) {
    return (
      <div className="detail-block">
        <div className="section-head">
          <span className="left">автозамена</span>
        </div>
        <div className="substitution-info" role="status">
          🔄 Заменена на:{' '}
          {children.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link to={`/job/${c.id}`}>
                #{c.id} ({c.station_code ?? '—'})
              </Link>
            </span>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// Phase 4.5 — auto-retry chain block.
//
// Mirrors :func:`DetailSubstitution` but for the
// model-substitution chain. No emoji — design-system rule from
// Phase 4.13.2 keeps the Mini App surface plain text. The
// ``.detail-block .section-head`` idiom is identical to the rest
// of DetailScreen; tag-link styling on the links matches the
// Phase-4.11 station/tag links.
//
// Two mutually-exclusive renders, same shape as substitution:
//   * Retry-job (``retry_parent_id`` set): «Авто-повтор задачи
//     #N (попытка X)».
//   * Root that spawned retries (``retry_children`` non-empty):
//     «Альтернативные модели: NNR-MORVEL56 → #M1, …» — one
//     line per child.
function DetailRetryChain({ job }: { job: JobDetail }) {
  if (job.retry_parent_id) {
    const labelModel = (job.tectonic_model ?? '').replace('_', '-');
    return (
      <div className="detail-block">
        <div className="section-head">
          <span className="left">авто-повтор</span>
        </div>
        <div className="retry-info" role="status">
          Повтор задачи{' '}
          <Link className="tag-link" to={`/job/${job.retry_parent_id}`}>
            #{job.retry_parent_id}
          </Link>{' '}
          с моделью {labelModel} (попытка {job.retry_attempt ?? 1}).
        </div>
      </div>
    );
  }
  const children = job.retry_children ?? [];
  if (children.length > 0) {
    return (
      <div className="detail-block">
        <div className="section-head">
          <span className="left">авто-повтор</span>
        </div>
        <ul className="retry-children-list">
          {children.map((c) => (
            <li key={c.id} className="retry-children-row">
              <span className="retry-model">
                {c.tectonic_model.replace('_', '-')}
              </span>
              <span className="retry-arrow" aria-hidden="true">
                →
              </span>
              <Link className="tag-link" to={`/job/${c.id}`}>
                #{c.id}
              </Link>
              <span className="retry-state">{c.state}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

// Phase 4.8a — collapsible RINEX header block.
//
// Renders the captured header (lines up to ``END OF HEADER``) inside
// a native ``<details>`` element so it occupies one row when
// collapsed. Default state:
//   * collapsed for jobs in any state other than
//     ``rinex_validation_status === 'incompatible'`` — header is a
//     verification aid, not primary information,
//   * OPEN for incompatible jobs because the operator opened the
//     screen precisely to investigate why gfzrnx said "no GNSS
//     systems we asked for"; the header is the first thing they
//     want to compare against.
//
// The ``<pre>`` keeps fixed-width formatting (RINEX headers are
// column-positional) and the CSS caps height at 400 px with vertical
// scrolling so a 5 KB header doesn't push the timeline off-screen.
function DetailRinexHeader({ job }: { job: JobDetail }) {
  if (!job.rinex_header_text) return null;
  const openByDefault = job.rinex_validation_status === 'incompatible';
  return (
    <details className="detail-block rinex-header-block" open={openByDefault}>
      {/* Phase 4.9 — flattened the original two-span ``.left`` /
          ``.right`` layout into a single span. The flex
          ``justify-content: space-between`` rule on ``.section-head``
          works in a normal block but inside ``<summary>`` the
          ``<details>`` element's intrinsic disclosure marker (even
          with ``list-style: none``) interferes with the flex
          container on mobile, collapsing the inter-span gap so the
          rendered text glued to «RINEXДЛЯ». A single span with an
          em-dash separator sidesteps the whole flex problem. */}
      <summary className="section-head rinex-header-summary">
        <span className="left">📄 шапка RINEX — для верификации парсера</span>
      </summary>
      <pre className="rinex-header-text">{job.rinex_header_text}</pre>
    </details>
  );
}

function DetailTimeline({ events }: { events: EventResponse[] }) {
  return (
    <div className="detail-block">
      <div className="section-head">
        <span className="left">хронология</span>
        <span className="right">{events.length} событий</span>
      </div>
      {events.length === 0 ? (
        <div className="timeline">
          <TimelineItem ts="—" type="info" text="нет событий" />
        </div>
      ) : (
        <div className="timeline">
          {[...events].reverse().map((e) => (
            <TimelineItem
              key={e.id}
              // Phase 3.10.1 — render in Moscow time. Backend serializes
              // UTC with Z suffix (Bug 1 fix); operators are in MSK so
              // a hard-coded zone here keeps the timeline readable
              // regardless of where the user opens the Mini App from.
              ts={formatTimeMoscow(e.ts)}
              type={e.severity as Severity}
              text={renderEventText(e)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function renderEventText(e: EventResponse): string {
  if (e.event_type === 'state_change' && e.payload) {
    const old_ = e.payload.old_state as string | null | undefined;
    const new_ = e.payload.new_state as string | null | undefined;
    // Phase 4.9 — wrap both endpoints with translateJobState so the
    // timeline reads «ожидает → отправлено на сервис» rather than the
    // raw English `pending → submitted`. Unknown future states fall
    // through to the verbatim key (see translateJobState fallback).
    if (old_ && new_) {
      return `${translateJobState(old_)} → ${translateJobState(new_)}`;
    }
    if (new_) return `→ ${translateJobState(new_)}`;
  }
  return e.message ?? e.event_type;
}

function DetailActions({ job }: { job: JobDetail }) {
  const qc = useQueryClient();
  const { tg } = useTelegramWebApp();
  const showCancel = job.state === 'pending' || job.state === 'submitted';
  // Retry is futile for not_applicable: the station structurally doesn't
  // write the requested system, so a re-run fails identically. Hide it
  // (other neutral categories — file_unavailable / no_reference / other —
  // keep Retry: a later run can still pick up a now-available file etc.).
  const showRetry =
    job.state === 'failed' && job.failure_category !== 'not_applicable';

  const invalidateAll = (updated: JobDetail) => {
    qc.setQueryData(['job', String(job.id)], updated);
    qc.invalidateQueries({ queryKey: ['jobs'] });
    qc.invalidateQueries({ queryKey: ['status'] });
    qc.invalidateQueries({ queryKey: ['events'] });
  };

  const cancelMut = useMutation({
    mutationFn: () => api.cancelJob(job.id),
    onSuccess: (updated) => {
      invalidateAll(updated);
      tg?.HapticFeedback?.notificationOccurred('success');
    },
    onError: (err) => {
      tg?.HapticFeedback?.notificationOccurred('error');
      const msg = err instanceof ApiError ? err.message : 'Ошибка отмены';
      if (tg?.showAlert) tg.showAlert(msg);
      else alert(msg);
    },
  });

  const retryMut = useMutation({
    mutationFn: () => api.retryJob(job.id),
    onSuccess: (updated) => {
      invalidateAll(updated);
      tg?.HapticFeedback?.notificationOccurred('success');
    },
    onError: (err) => {
      tg?.HapticFeedback?.notificationOccurred('error');
      const msg = err instanceof ApiError ? err.message : 'Ошибка повтора';
      if (tg?.showAlert) tg.showAlert(msg);
      else alert(msg);
    },
  });

  const onCancelClick = () => {
    const message = `Отменить задачу #${job.id}?`;
    if (tg?.showConfirm) {
      tg.showConfirm(message, (ok: boolean) => {
        if (ok) cancelMut.mutate();
      });
    } else if (window.confirm(message)) {
      cancelMut.mutate();
    }
  };

  if (!showCancel && !showRetry) return null;
  return (
    <div className="action-row">
      {showCancel && (
        <button
          className="btn danger"
          onClick={onCancelClick}
          disabled={cancelMut.isPending}
        >
          <Ic.close />
          {cancelMut.isPending ? 'Отменяю…' : 'Отменить'}
        </button>
      )}
      {showRetry && (
        <button
          className="btn primary"
          onClick={() => retryMut.mutate()}
          disabled={retryMut.isPending}
        >
          <Ic.refresh />
          {retryMut.isPending ? 'Перезапускаю…' : 'Повторить'}
        </button>
      )}
    </div>
  );
}
